<template>
  <div class="call-banner-header">
    <div class="call-banner-info">
      <i class="fas fa-phone"></i>
      <div class="call-banner-details">
        <div class="call-banner-title" data-testid="call-banner-title">
          {{ title }}
          <span class="call-timer">| DUREE : {{ formatTimer(callDuration) }}</span>
        </div>
        <div class="call-banner-quote" v-if="quote">
          {{ formatPhone(quote.telephone) }} - {{ quote.nom ? quote.nom.toUpperCase() : (quote.raison_sociale || 'Client') }}
          <router-link 
            v-if="quoteId && !isOnQuotePage" 
            :to="`/quotes/${quoteId}`" 
            class="quote-link"
            @click.stop
          >
            <i class="fas fa-external-link-alt"></i>
            Voir le devis
          </router-link>
        </div>
      </div>
    </div>
    <div class="call-banner-header-actions">
      <Button
        variant="secondary"
        icon="fas fa-check-circle"
        @click.stop="$emit('validate-info')"
        size="sm"
        class="btn-call-analyse"
        data-testid="call-validate-info"
      >
        Analyse du besoin
      </Button>
      <Button
        v-if="callDirection !== 'entrant'"
        variant="secondary"
        icon="fas fa-phone-slash"
        @click.stop="$emit('mark-no-answer')"
        :disabled="savingCall"
        size="sm"
        data-testid="call-mark-no-answer"
      >
        Ne répond pas
      </Button>
      <Button
        variant="secondary"
        icon="fas fa-calendar-check"
        @click.stop="$emit('schedule-relance')"
        :disabled="savingCall"
        size="sm"
        class="btn-call-relance"
        data-testid="call-schedule-relance"
      >
        Programmer une relance
      </Button>
      <Button
        variant="secondary"
        icon="fas fa-ban"
        @click.stop="$emit('mark-not-interested')"
        :disabled="savingCall"
        size="sm"
        class="btn-call-lost"
        data-testid="call-mark-not-interested"
      >
        Pas intéressé
      </Button>
    </div>
    <button
      type="button"
      class="call-abandon-btn"
      @click.stop="$emit('abandon-call')"
      :disabled="savingCall"
      aria-label="Annuler l'appel"
      data-testid="call-abandon-button"
    >
      <i class="fas fa-times"></i>
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { Button } from '../ui'
import { formatPhone } from '../../utils/formatting.js'

const route = useRoute()

const props = defineProps({
  callDuration: {
    type: Number,
    default: 0
  },
  quote: {
    type: Object,
    default: null
  },
  quoteId: {
    type: [String, Number],
    default: null
  },
  savingCall: {
    type: Boolean,
    default: false
  },
  callDirection: {
    type: String,
    default: 'sortant'
  },
  formatTimer: {
    type: Function,
    required: true
  }
})

const isOnQuotePage = computed(() => {
  if (!props.quoteId) return false
  return route.path === `/quotes/${props.quoteId}`
})

const title = computed(() => {
  if (props.callDirection === 'entrant') return 'APPEL ENTRANT EN COURS'
  if (props.callDirection === 'visio') return 'VISIO-CONFÉRENCE EN COURS'
  return 'APPEL SORTANT EN COURS'
})

defineEmits(['mark-no-answer', 'mark-not-interested', 'schedule-relance', 'abandon-call', 'validate-info'])
</script>

<style scoped>
.call-banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md) var(--spacing-lg);
  padding: var(--spacing-md);
  /* Réserve le coin haut-droit pour la croix (positionnée en absolu) */
  padding-right: 3rem;
  user-select: none;
  min-height: 80px;
  position: relative;
  /* Permet au bloc d'actions de passer sous les infos si le bandeau est trop étroit */
  flex-wrap: wrap;
}

.call-banner-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  /* Garde une largeur mini avant de laisser les boutons wrapper sous les infos */
  flex: 1 1 260px;
  min-width: 0;
}

.call-banner-info i {
  font-size: 1.5rem;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

.call-banner-details {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.call-direction-toggle {
  display: flex;
  gap: 0;
  background: rgba(0, 0, 0, 0.2);
  border-radius: var(--radius-sm);
  overflow: hidden;
  width: fit-content;
}

.call-direction-toggle :deep(.btn) {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 0;
  white-space: nowrap;
  box-shadow: none;
}

.call-direction-toggle :deep(.btn:hover) {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
}

.call-direction-toggle :deep(.btn.active) {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.call-direction-toggle :deep(.btn .btn-icon) {
  font-size: 0.65rem;
  animation: none;
}

.call-banner-title {
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.call-timer {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  font-size: 1rem;
}

.call-banner-quote {
  font-size: 0.9rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
}

.quote-link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: white;
  text-decoration: underline;
  font-size: 0.85rem;
  opacity: 0.9;
  transition: opacity var(--transition-base);
  margin-left: var(--spacing-sm);
}

.quote-link:hover {
  opacity: 1;
  text-decoration: none;
}

.quote-link i {
  font-size: 0.75rem;
}

.call-banner-header-actions {
  display: flex;
  gap: var(--spacing-sm);
  align-items: center;
  /* Boutons sur deux lignes (alignés à droite) si la largeur est insuffisante */
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Couleurs sémantiques des issues d'appel, lisibles sur le bandeau rouge.
   Préfixé par .call-banner-header-actions pour dépasser la spécificité de
   .btn-secondary:hover:not(:disabled) (buttons.css) qui, à égalité, réécrasait
   le fond et repassait le bouton en clair. */
.call-banner-header-actions :deep(.btn-call-analyse) {
  background: var(--color-info);
  border-color: var(--color-info);
  color: white;
}

.call-banner-header-actions :deep(.btn-call-analyse:hover:not(:disabled)) {
  background: var(--color-info-dark);
  border-color: var(--color-info-dark);
  color: white;
}

.call-banner-header-actions :deep(.btn-call-relance) {
  background: var(--color-warning);
  border-color: var(--color-warning);
  color: var(--color-text-primary);
}

.call-banner-header-actions :deep(.btn-call-relance:hover:not(:disabled)) {
  background: var(--color-warning-dark);
  border-color: var(--color-warning-dark);
  color: var(--color-text-primary);
}

.call-banner-header-actions :deep(.btn-call-lost) {
  background: var(--color-danger-darker);
  border-color: rgba(255, 255, 255, 0.35);
  color: white;
}

.call-banner-header-actions :deep(.btn-call-lost:hover:not(:disabled)) {
  background: var(--color-danger-dark);
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.call-abandon-btn {
  position: absolute;
  top: var(--spacing-sm);
  right: var(--spacing-sm);
  background: transparent;
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity var(--transition-base), background var(--transition-base);
  z-index: 1;
}

.call-abandon-btn:hover:not(:disabled) {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

.call-abandon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.call-abandon-btn i {
  font-size: 1.1rem;
}

@media (max-width: 768px) {
  .call-banner-header {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm);
    min-height: auto;
    padding: var(--spacing-sm);
  }
  
  .call-banner-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .call-banner-header-actions {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .call-banner-header-actions :deep(.btn) {
    flex: 1;
    min-width: 120px;
  }
  
  .call-banner-title {
    font-size: 0.9rem;
  }
  
  .call-timer {
    font-size: 0.9rem;
  }
  
  .call-banner-quote {
    font-size: 0.85rem;
  }
}
</style>
